'use client';

import { useMemo, useRef, useState } from 'react';
import { TZDate } from '@date-fns/tz';
import { format } from 'date-fns';
import { ChevronDownIcon, FunctionSquareIcon, Headset, User } from 'lucide-react';

import { AudioPlayer } from '@/components/audio-player';
import { useAuthCtx } from '@/components/providers/auth-provider';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import { SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  isMessageItem,
  isTool,
  Message,
  ToolCallInvocation,
  ToolCallResult,
  TranscriptItem,
} from '@/consts/call-types';
import { formatJson, formatPhoneNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

type Call = ApiSchemas['CallRead'];

interface CallDialogContentLoaderProps {
  callId: string;
  isOpen: boolean;
}

export function CallDialogContentLoader({ callId, isOpen }: CallDialogContentLoaderProps) {
  const { api } = useApiV1Ctx();
  const { data, isLoading } = api.calls.getCallsIdGet.useQuery(
    {
      path: { id: callId },
    },
    {
      enabled: isOpen,
    }
  );

  return (
    <SheetContent className='w-full sm:max-w-lg' showClose>
      {isLoading ? (
        <>
          <SheetTitle className='sr-only'>Call Loading</SheetTitle>
          <Skeleton className='bg-muted/60' />
        </>
      ) : data ? (
        <CallDialogContentInner data={data} />
      ) : (
        <div className='flex h-full w-full items-center justify-center'>
          <SheetTitle>Call not found</SheetTitle>
        </div>
      )}
    </SheetContent>
  );
}

type CallDialogContentProps = {
  data: Call;
};

export function CallDialogContent({ data }: CallDialogContentProps) {
  return (
    <SheetContent
      className='w-full sm:max-w-lg'
      onInteractOutside={(e) => {
        e.preventDefault();
      }}
      showClose
    >
      <CallDialogContentInner data={data} />
    </SheetContent>
  );
}

function CallDialogContentInner({ data }: CallDialogContentProps) {
  const { isSuperuser } = useAuthCtx();
  const audioRef = useRef<HTMLAudioElement>(null);
  const phoneNumber = useMemo(() => formatPhoneNumber(data.from_number), [data]);
  const started = useMemo(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return new TZDate(data.start_timestamp, tz);
  }, [data]);
  const transcript = useMemo(() => {
    if (!data.transcript_with_tool_calls) return [];
    /* join consecutive messages with user or agent role */
    const out: TranscriptItem[] = [];
    for (const message of data.transcript_with_tool_calls) {
      const msg = { ...message } as TranscriptItem;
      if (isTool(msg) && isSuperuser) {
        out.push(msg);
        continue;
      }
      if (isMessageItem(msg)) {
        const last = out[out.length - 1];
        if (!last || last.role !== msg.role) {
          out.push(msg);
          continue;
        }
        last.content += ` ${msg.content}`;
      }
    }
    return out;
  }, [data, isSuperuser]);

  return (
    <>
      <div className='bg-background flex flex-col gap-4 rounded-lg'>
        <div className='flex flex-col gap-3 p-4 pb-0'>
          <div className='space-y-1.5'>
            <SheetTitle className='text-2xl font-normal'>Call details for {phoneNumber}</SheetTitle>
            <SheetDescription>
              {format(started, 'hh:mm a')} - {format(started, 'MMM d, yyyy')}
            </SheetDescription>
          </div>

          {data.recording_url && (
            <AudioPlayer ref={audioRef} className='w-full' url={data.recording_url} key={data.recording_url} />
          )}
        </div>

        <hr />
      </div>

      <div className='flex w-full flex-1 flex-col items-start justify-start space-y-4 overflow-auto px-4 pt-0'>
        <div className='space-y-2 px-2'>
          <div className='font-semibold'>Call summary</div>
          <div className='bg-secondary rounded-xl p-4'>{data.retell_analysis?.call_summary}</div>
        </div>

        <div className='w-full px-2'>
          <hr className='w-full' />
        </div>

        {/* <AdminOnly>
          <CallComments call={data} className='px-2' />
          <div className='w-full px-2'>
            <hr className='w-full' />
          </div>
        </AdminOnly> */}

        <div className='px-2 font-semibold'>Transcript</div>

        <div className='w-full space-y-2 pb-8'>
          {transcript.map((message, index) => {
            if (isTool(message)) {
              return <ToolBubble item={message} key={`${message.role}-message-${index}`} />;
            }
            if (isMessageItem(message)) {
              return (
                <ChatBubble
                  message={message}
                  key={`${message.role}-message-${index}`}
                  onClick={() => {
                    if (audioRef.current) {
                      audioRef.current.currentTime = message.words[0].start;
                      audioRef.current.play();
                    }
                  }}
                />
              );
            }
            return null;
          })}
        </div>
      </div>
    </>
  );
}

interface ToolBubbleProps {
  item: ToolCallInvocation | ToolCallResult;
}

function ToolBubble({ item }: ToolBubbleProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const content = useMemo(() => {
    let out = '';
    if (item.role === 'tool_call_invocation') {
      out = item.arguments;
    } else if (item.role === 'tool_call_result') {
      out = item.content;
    }
    return formatJson(out);
  }, [item]);

  return (
    <div className='flex w-full flex-col gap-1.5'>
      <div
        className={cn('flex w-full gap-3 rounded-md p-2', 'hover:bg-muted transition-colors hover:cursor-pointer')}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div
          className={cn(
            'bg-secondary text-secondary-foreground flex size-10 shrink-0 items-center justify-center rounded-full',
            item.role === 'tool_call_invocation' && 'bg-blue-500/10 text-blue-500',
            item.role === 'tool_call_result' && 'bg-green-500/10 text-green-500'
          )}
        >
          <FunctionSquareIcon className='size-5' />
        </div>
        <div className='w-full space-y-1.5'>
          <div className={cn('flex w-full items-center justify-between')}>
            <div className='text-sm font-semibold'>
              {item.role === 'tool_call_invocation' ? item.name : 'Tool call result'}
            </div>
            <Button variant='ghost' size='icon'>
              <ChevronDownIcon className='size-4' />
            </Button>
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className='flex w-full gap-3'>
          <div className='w-10' />
          <div className='bg-muted w-full rounded-md p-3 text-xs whitespace-pre-wrap'>{content}</div>
        </div>
      )}
    </div>
  );
}

interface ChatBubbleProps {
  message: Message;
  onClick?: () => void;
}

function ChatBubble({ message, onClick }: ChatBubbleProps) {
  const Icon = message.role === 'user' ? User : Headset;
  const time = useMemo(() => {
    const start = message.words[0].start;
    if (!start) return null;
    const minutes = Math.floor(start / 60);
    const seconds = Math.floor(start % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [message.words]);

  return (
    <div
      className={cn('flex w-full gap-3 rounded-md p-2', 'hover:bg-muted transition-colors hover:cursor-pointer')}
      onClick={onClick}
    >
      <div
        className={cn(
          'bg-secondary text-secondary-foreground flex size-10 shrink-0 items-center justify-center rounded-full',
          message.role === 'user' && 'bg-primary/80 dark:bg-primary text-primary-foreground'
        )}
      >
        <Icon className='size-5' />
      </div>
      <div className='space-y-1'>
        <div className='text-sm capitalize'>
          <span className='font-medium'>{message.role}</span>
          <span className='text-muted-foreground ml-1.5 text-xs'>{time}</span>
        </div>
        <div className='text-base'>{message.content}</div>
      </div>
    </div>
  );
}
